import { h, empty } from './dom.js';

/**
 * LIVE NOW card — shared by the library sidebar and the homepage.
 *
 * Fetches currently-active solo rooms from /api/rooms/active and
 * renders a compact list of them. Polls every 30s while mounted so
 * the card stays fresh. Click-through goes to /u/<host>/watch on the
 * main app.
 *
 * Mounts idempotently: calling mountLiveNowCard again re-renders into
 * the same container without setting up duplicate timers (we stash
 * the timer on the container via a WeakMap).
 *
 * Options:
 *   maxRows = 5          — cap the row count; above this, "+N more →"
 *   moreHref = '/'       — where "+N more" links to (homepage by default;
 *                          the library passes '/' to send users back to the
 *                          homepage LIVE NOW card)
 *   hideWhenEmpty = true — collapse to display:none when no rooms are active
 */

const POLL_MS = 30_000;
const timers = new WeakMap();

export function mountLiveNowCard(container, opts = {}) {
  const { maxRows = 5, moreHref = '/', hideWhenEmpty = true } = opts;

  // Kill any previous timer if this container was re-used.
  const prev = timers.get(container);
  if (prev) clearInterval(prev);

  load();
  const t = setInterval(load, POLL_MS);
  timers.set(container, t);

  // If the container is ever detached from the DOM (sidebar re-render),
  // garbage-collect the timer so we don't keep fetching into the void.
  const observer = new MutationObserver(() => {
    if (!container.isConnected) {
      clearInterval(t);
      timers.delete(container);
      observer.disconnect();
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  async function load() {
    try {
      const res = await fetch('/api/rooms/active');
      if (!res.ok) throw new Error();
      const data = await res.json();
      const rooms = data.rooms || [];
      render(rooms);
    } catch {
      // Server down / route disabled — hide the card silently.
      empty(container);
      if (hideWhenEmpty) container.style.display = 'none';
    }
  }

  function render(rooms) {
    if (rooms.length === 0) {
      // No live games — collapse the card entirely so empty space
      // doesn't linger.
      empty(container);
      if (hideWhenEmpty) container.style.display = 'none';
      return;
    }
    container.style.display = '';
    empty(container);
    container.appendChild(buildCard(rooms, maxRows, moreHref));
  }
}

function buildCard(rooms, maxRows, moreHref) {
  const shown = rooms.slice(0, maxRows);
  const extra = rooms.length - shown.length;

  const card = h('div', { class: 'lib-live-now-card' },
    h('h3', { class: 'lib-sidebar-heading' },
      h('span', { class: 'lib-live-dot', 'aria-hidden': 'true' }, ''),
      'Live now',
    ),
    h('div', { class: 'lib-live-rows' },
      ...shown.map(renderRow),
      extra > 0 ? h('a', {
        class: 'lib-live-more',
        href: moreHref,
      }, `+${extra} more →`) : null,
    ),
  );
  return card;
}

function renderRow(room) {
  const title = room.gameTitle || room.romTitle || room.romName || 'Unknown game';
  const host = room.hostUsername || 'anonymous';
  // Clicking a row jumps to the host's watch URL in the main app.
  // location.assign (not an <a> href) because the library is a separate
  // app — a normal link would be same-origin but different app bundle,
  // and we want the full main-app boot, not a library route match.
  return h('button', {
    type: 'button',
    class: 'lib-live-row',
    title: `Watch ${host} play ${title}`,
    onclick: () => {
      if (room.hostUsername) {
        location.assign(`/u/${encodeURIComponent(room.hostUsername)}/play`);
      }
    },
  },
    h('span', { class: 'lib-live-row-title' }, title),
    h('span', { class: 'lib-live-row-meta' },
      host,
      room.spectatorCount > 0 ? h('span', { class: 'lib-live-row-viewers' }, `👁 ${room.spectatorCount}`) : null,
    ),
  );
}
